/**
 * UDC (Universal Data Component) API Service
 *
 * Generic service for all UDC collections (tickets, leads, users, etc.)
 */

import apiClient from '@/api/client';
import type {
  UDCResponse,
  UDCRequestParams,
  BulkSelectRequest,
  BulkSelectResponse,
  BulkFilterRequest,
} from '@/api/types/udc.types';

/**
 * Generic UDC service for all collections
 */
export const udcService = {
  /**
   * Fetch table data for any collection
   * @param collection - Collection name (e.g., 'tickets', 'leads', 'bp')
   * @param params - UDC request parameters
   */
  async fetchTableData<T = any>(
    collection: string,
    params: UDCRequestParams
  ): Promise<UDCResponse<T>> {
    // Use GET with query parameters (natural query parameter format)
    // Most collections use /{collection}/udc, but tickets uses /tickets directly
    const endpoint =
      collection === 'tickets' ? `/${collection}` : `/${collection}/udc`;
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  },

  /**
   * Select multiple items for bulk actions
   * @param collection - Collection name
   * @param selectionId - UUID for this selection session
   * @param itemIds - Array of item IDs to select
   */
  async selectItems(
    collection: string,
    selectionId: string,
    itemIds: string[]
  ): Promise<BulkSelectResponse> {
    const response = await apiClient.patch(
      `/${collection}/bulk-select/${selectionId}/items/select-multiple`,
      { item_ids: itemIds } as BulkSelectRequest
    );
    return response.data;
  },

  /**
   * Deselect multiple items
   * @param collection - Collection name
   * @param selectionId - UUID for this selection session
   * @param itemIds - Array of item IDs to deselect
   */
  async deselectItems(
    collection: string,
    selectionId: string,
    itemIds: string[]
  ): Promise<BulkSelectResponse> {
    const response = await apiClient.patch(
      `/${collection}/bulk-select/${selectionId}/items/clear-multiple`,
      { item_ids: itemIds } as BulkSelectRequest
    );
    return response.data;
  },

  /**
   * Select all items on current page
   * @param collection - Collection name
   * @param selectionId - UUID for this selection session
   */
  async selectAll(
    collection: string,
    selectionId: string
  ): Promise<BulkSelectResponse> {
    const response = await apiClient.patch(
      `/${collection}/bulk-select/${selectionId}/select-all`
    );
    return response.data;
  },

  /**
   * Select items by filter
   * @param collection - Collection name
   * @param selectionId - UUID for this selection session
   * @param filterRequest - Filter criteria
   */
  async selectByFilter(
    collection: string,
    selectionId: string,
    filterRequest: BulkFilterRequest
  ): Promise<BulkSelectResponse> {
    const response = await apiClient.patch(
      `/${collection}/bulk-select/${selectionId}/filter`,
      filterRequest
    );
    return response.data;
  },

  /**
   * Clear all selections
   * @param collection - Collection name
   * @param selectionId - UUID for this selection session
   */
  async clearSelection(collection: string, selectionId: string): Promise<void> {
    await apiClient.delete(`/${collection}/bulk-select/${selectionId}`);
  },

  /**
   * Execute bulk action
   * @param url - Action URL from backend
   * @param method - HTTP method
   * @param data - Request data
   */
  async executeBulkAction(
    url: string,
    method: string,
    data?: any
  ): Promise<any> {
    const response = await apiClient.request({
      url,
      method,
      data,
    });
    return response.data;
  },
};
